import type { FunctionComponent } from "react"

import { usePathname, useSearchParams } from "next/navigation"

import { ArticleCard } from "@jobteaser/ui-kit/components/Card/cardComponents/ArticleCard/ArticleCard"
import { ElementGrid } from "@jobteaser/ui-kit/components/ElementGrid/ElementGrid"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"

import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { LIST_ITEMS_PER_PAGE, PREFILTERED_BASENAME } from "@/modules/jobSearchPrefiltered/constants"
import { useActiveSlugsFetch } from "@/modules/jobSearchPrefiltered/hooks/useActiveSlugsFetch"
import { firstLetterToUpperCase } from "@/modules/jobSearchPrefiltered/utils/firstLetterToUpperCase"
import { getLocalizedContentKeysForSlug } from "@/modules/jobSearchPrefiltered/utils/getLocalizedContentKeysForSlug"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { ShallowLink } from "@/modules/routing/components/ShallowLink"
import { Pagination } from "@/modules/uiKitOverrides/Pagination/Pagination"

export const ActiveSlugs: FunctionComponent = () => {
  const { dataActiveSlugs } = useActiveSlugsFetch()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const { t } = useTranslation(["job_search_prefiltered"])

  if (!dataActiveSlugs) {
    return null
  }

  const { slugs, totalCount } = dataActiveSlugs

  return (
    <Layout
      as="section"
      colorBlock={LAYOUT_COLOR_BLOCK.GREY}
      hasTopPadding
      hasBottomPadding
      data-testid="PrefilteredJobSearch-List__list"
    >
      <Layout.Column>
        <ElementGrid>
          {slugs.map(data => {
            const { slug } = data
            const { title, shortDescription } = getLocalizedContentKeysForSlug(data)

            return (
              <ArticleCard
                data-testid="PrefilteredJobSearch-List__list-item"
                description={t(shortDescription, data.params)}
                key={slug}
                linkProps={{
                  as: Link,
                  href: `/${locale}/${PREFILTERED_BASENAME}/${slug}`,
                }}
                // We cannot use the ::first-letter CSS selector because there is a pseudo element used in the
                // component so we do it directly in the text.
                title={firstLetterToUpperCase(t(title, data.params))}
              />
            )
          })}
        </ElementGrid>

        {totalCount > LIST_ITEMS_PER_PAGE ? (
          <Pagination
            itemPerPage={LIST_ITEMS_PER_PAGE}
            linkComponent={ShallowLink}
            linkParam="href"
            linkPathname={pathname}
            linkSearchParams={searchParams.toString()}
            totalCount={totalCount}
          />
        ) : null}
      </Layout.Column>
    </Layout>
  )
}
